import { useMemo } from 'react';
import { AutoResizer, Column, ColumnShape } from 'react-base-table';
import { useRecoilValue } from 'recoil';

import ELEMENT, { createTestId } from 'common/element';
import { CellType, Column as ColumnType } from 'common/graphqlTypes';
import {
  DEFAULT_CELL_DISPLAY_VALUE,
  getDisplayValue,
  isPQLinkedValue,
  PQBaseDataValue,
  PQDataValue,
  PQRowValue,
} from 'common/util/pqUtil';

import BaseTable from 'components/common/BaseTable/BaseTable';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import {
  getCellRendererState,
  getMergedSchemaFieldsState,
  viewFieldsState,
} from 'components/common/Managers/ViewStateManager.state';
import { completePowerQueryState } from 'components/common/Managers/RouteStateManager.state';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';

import { ASSUMED_ROW_HEIGHT, AVERAGE_NUMBER_OF_ROWS, BORDERS_WIDTH } from '../../consts';
import { getColumnsWidth, ID_COLUMN_KEY, prepareData } from '../../helpers';

const rowProps = ({ rowIndex }: { rowIndex: number }) => ({
  'data-test-id': createTestId(ELEMENT.powerQueryTableRow, String(rowIndex)),
});

export type PQTableProps = {
  rowValues: PQDataValue[][];
  headers: ColumnType[];
  testId?: string;
};

const PQTable = ({ rowValues, headers, testId }: PQTableProps) => {
  const timezone = useRecoilValue(timezoneState);
  const columnSpecs = useRecoilValue(viewFieldsState);
  const getCellRenderer = useRecoilValue(getCellRendererState);
  const getMergedSchemaFields = useRecoilValue(getMergedSchemaFieldsState);
  const { addedColumns } = useRecoilValue(completePowerQueryState);

  // Use the label from a schema as the column header if available, and the description as the tooltip.
  const headerRenderer = ({ column }: { column: ColumnShape }) => {
    const label = columnSpecs[column.field]?.label ?? '';
    const description = columnSpecs[column.field]?.description ?? '';
    return (
      <ElementWithTooltip tooltip={description || label || column.title} placement="top">
        <span>{label || column.title}</span>
      </ElementWithTooltip>
    );
  };

  const widths = useMemo(() => {
    const renderedRowValues = rowValues.slice(0, AVERAGE_NUMBER_OF_ROWS).map((row) =>
      row.map((cell, i) => {
        if (!cell) {
          return DEFAULT_CELL_DISPLAY_VALUE;
        }

        if (isPQLinkedValue(cell)) {
          return cell.value || DEFAULT_CELL_DISPLAY_VALUE;
        }

        return getDisplayValue(cell, headers[i], timezone);
      }),
    );

    return getColumnsWidth(headers, renderedRowValues);
  }, [headers, rowValues, timezone]);

  const columns = useMemo<ColumnShape<Record<string, PQBaseDataValue>>[]>(
    () =>
      headers
        // don't show user columns we added to help render other columns
        .filter((header) => !addedColumns.includes(header.name))
        .map(({ format, name, decimalPlaces, type }) => ({
          id: name,
          width: columnSpecs[name]?.width ?? widths[name],
          align: type === CellType.NUMBER ? Column.Alignment.RIGHT : Column.Alignment.LEFT,
          cellRenderer: ({
            cellData,
            column,
            rowData,
          }: {
            cellData: PQDataValue;
            column: ColumnType;
            rowData: PQRowValue;
          }) => {
            const Renderer = getCellRenderer(name, null, true);
            if (Renderer) {
              return (
                <Renderer
                  row={rowData}
                  fieldId={name}
                  schemaFields={getMergedSchemaFields(null, true)}
                  value={cellData}
                />
              );
            }
            return getDisplayValue(cellData, column, timezone);
          },
          resizable: true,
          key: name,
          title: columnSpecs[name]?.label ?? name,
          dataGetter: ({ rowData }): PQBaseDataValue => rowData[name],
          decimalPlaces,
          format,
          headerRenderer,
        })),
    [headers, widths, timezone],
  );

  const data = useMemo(() => prepareData(rowValues, headers), [rowValues, headers]);

  return (
    <div className="PowerQueries__TableWrapper" data-test-id={testId}>
      <AutoResizer>
        {({ width, height }) => (
          <BaseTable
            fixed
            width={width - BORDERS_WIDTH}
            height={height}
            columns={columns}
            estimatedRowHeight={ASSUMED_ROW_HEIGHT}
            data={data}
            rowKey={ID_COLUMN_KEY}
            rowProps={rowProps}
          />
        )}
      </AutoResizer>
    </div>
  );
};

export default PQTable;
